import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../../api/api";
import Swal from "sweetalert2";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  CheckCircle,
  Delete,
  Description,
  Edit,
  FilterAlt,
  LibraryBooks,
  Publish,
  Refresh,
  Search,
  School,
  Subject as SubjectIcon,
  ToggleOff,
  ToggleOn,
  CloudDownload,
} from "@mui/icons-material";

import CurriculumPost from "./CurriculumPost";


const DOCUMENT_TYPES = {
  curriculum: "National Curriculum",
  syllabus: "Syllabus",
  teacher_guide: "Teacher Guide",
  framework: "Curriculum Framework",
  assessment: "Assessment Guide",
  other: "Other",
};


const GENERIC_CURRICULUM_TITLES = new Set([
  "general",
  "general curriculum",
  "curriculum",
  "national curriculum",
  "official curriculum",
  "national curriculum document",
  "curriculum document",
]);


const getErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    return (
      error?.message ||
      "An unexpected error occurred."
    );
  }

  if (typeof data === "string") {
    return data;
  }

  if (data.detail) {
    return data.detail;
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.join(", ")}`;
        }

        if (
          value &&
          typeof value === "object"
        ) {
          return `${key}: ${JSON.stringify(value)}`;
        }

        return `${key}: ${value}`;
      })
      .join("\n");
  }

  return "Unable to complete the request.";
};


const normalizeItems = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  return [];
};


const cleanFilenameLabel = (value) => {
  if (!value) {
    return "";
  }

  let label = String(value).trim();

  label = label
    .replace(/\.[^/.]+$/, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return label;
};


const getCurriculumLabel = (document) => {
  if (!document) {
    return "Curriculum";
  }

  const backendLabel =
    document.curriculum_label ||
    document.display_title ||
    document.curriculum_file_label ||
    document.filename_without_extension;

  if (backendLabel) {
    return cleanFilenameLabel(
      backendLabel
    );
  }

  const filename =
    document.filename ||
    document.file_name ||
    document.original_filename;

  const filenameLabel =
    cleanFilenameLabel(filename);

  const title =
    typeof document.title === "string"
      ? document.title.trim()
      : "";

  const normalizedTitle =
    title
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  if (
    filenameLabel &&
    (
      !title ||
      GENERIC_CURRICULUM_TITLES.has(
        normalizedTitle
      )
    )
  ) {
    return filenameLabel;
  }

  if (title) {
    return title;
  }

  if (filenameLabel) {
    return filenameLabel;
  }

  return "Curriculum";
};


const getFilename = (document) => {
  if (!document) {
    return "";
  }

  return (
    document.filename ||
    document.file_name ||
    document.original_filename ||
    ""
  );
};


const getSubjectName = (document) =>
  document?.subject_name ||
  document?.display_subject ||
  document?.subject_label ||
  document?.subject_data?.name ||
  document?.subject?.name ||
  "General";


const getGradeName = (document) =>
  document?.grade_class_name ||
  document?.grade_name ||
  document?.grade_class_data?.name ||
  document?.grade_class?.name ||
  "—";


const getAcademicYearName = (document) =>
  document?.academic_year_name ||
  document?.academic_year_data?.name ||
  document?.academic_year_data?.year ||
  (
    typeof document?.academic_year === "object"
      ? document.academic_year?.name
      : document?.academic_year
  ) ||
  "—";


const getDocumentTypeName = (document) =>
  DOCUMENT_TYPES[
    document?.document_type
  ] ||
  document?.document_type ||
  "Other";


const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );
};


const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
  );
};


const formatFileSize = (document) => {
  if (
    document?.file_size_mb !== undefined &&
    document?.file_size_mb !== null
  ) {
    return `${document.file_size_mb} MB`;
  }

  const bytes = Number(
    document?.file_size || 0
  );

  if (!bytes) {
    return "—";
  }

  const mb =
    bytes /
    (1024 * 1024);

  return `${mb.toFixed(2)} MB`;
};


const getFileExtension = (document) => {
  if (document?.extension) {
    return String(
      document.extension
    ).toUpperCase();
  }

  const filename =
    getFilename(document);

  if (!filename) {
    return "FILE";
  }

  const parts =
    filename.split(".");

  if (parts.length < 2) {
    return "FILE";
  }

  return parts
    .pop()
    .toUpperCase();
};


const AllCurriculumLis = () => {
  const [documents, setDocuments] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [gradeFilter, setGradeFilter] =
    useState("");

  const [subjectFilter, setSubjectFilter] =
    useState("");

  const [academicYearFilter, setAcademicYearFilter] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("");

  const [typeFilter, setTypeFilter] =
    useState("");

  const [page, setPage] =
    useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  const [editingDocument, setEditingDocument] =
    useState(null);

  const [showEditor, setShowEditor] =
    useState(false);


  const loadCurriculums = async (
    showRefresh = false
  ) => {
    if (showRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const response = await api.get(
        "/national-curriculum/",
        {
          params: {
            page_size: 1000,
          },
        }
      );

      setDocuments(
        normalizeItems(response)
      );
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError
        )
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  useEffect(() => {
    loadCurriculums();
  }, []);


  const gradeOptions = useMemo(() => {
    return [
      ...new Set(
        documents
          .map(getGradeName)
          .filter(
            (value) =>
              value &&
              value !== "—"
          )
      ),
    ].sort(
      (a, b) =>
        a.localeCompare(b)
    );
  }, [documents]);


  const subjectOptions = useMemo(() => {
    return [
      ...new Set(
        documents
          .map(getSubjectName)
          .filter(Boolean)
      ),
    ].sort(
      (a, b) =>
        a.localeCompare(b)
    );
  }, [documents]);


  const academicYearOptions =
    useMemo(() => {
      return [
        ...new Set(
          documents
            .map(
              getAcademicYearName
            )
            .filter(
              (value) =>
                value &&
                value !== "—"
            )
        ),
      ].sort(
        (a, b) =>
          a.localeCompare(b)
      );
    }, [documents]);


  const filteredDocuments = useMemo(() => {
    const query =
      search
        .trim()
        .toLowerCase();

    return documents.filter(
      (document) => {
        const curriculumLabel =
          getCurriculumLabel(
            document
          );

        const filename =
          getFilename(
            document
          );

        const subjectName =
          getSubjectName(
            document
          );

        const gradeName =
          getGradeName(
            document
          );

        const academicYear =
          getAcademicYearName(
            document
          );

        const documentType =
          getDocumentTypeName(
            document
          );

        const matchesSearch =
          !query ||
          curriculumLabel
            .toLowerCase()
            .includes(query) ||
          filename
            .toLowerCase()
            .includes(query) ||
          (
            document.title ||
            ""
          )
            .toLowerCase()
            .includes(query) ||
          (
            document.document_code ||
            ""
          )
            .toLowerCase()
            .includes(query) ||
          (
            document.description ||
            ""
          )
            .toLowerCase()
            .includes(query) ||
          subjectName
            .toLowerCase()
            .includes(query) ||
          gradeName
            .toLowerCase()
            .includes(query) ||
          academicYear
            .toLowerCase()
            .includes(query) ||
          documentType
            .toLowerCase()
            .includes(query);

        const matchesGrade =
          !gradeFilter ||
          gradeName ===
            gradeFilter;

        const matchesSubject =
          !subjectFilter ||
          subjectName ===
            subjectFilter;

        const matchesAcademicYear =
          !academicYearFilter ||
          academicYear ===
            academicYearFilter;

        const matchesType =
          !typeFilter ||
          document.document_type ===
            typeFilter;

        let matchesStatus = true;

        if (
          statusFilter ===
          "published"
        ) {
          matchesStatus =
            document.is_published ===
            true;
        }

        if (
          statusFilter ===
          "draft"
        ) {
          matchesStatus =
            document.is_published ===
            false;
        }

        if (
          statusFilter ===
          "active"
        ) {
          matchesStatus =
            document.is_active ===
            true;
        }

        if (
          statusFilter ===
          "inactive"
        ) {
          matchesStatus =
            document.is_active ===
            false;
        }

        return (
          matchesSearch &&
          matchesGrade &&
          matchesSubject &&
          matchesAcademicYear &&
          matchesType &&
          matchesStatus
        );
      }
    );
  }, [
    documents,
    search,
    gradeFilter,
    subjectFilter,
    academicYearFilter,
    typeFilter,
    statusFilter,
  ]);


  const paginatedDocuments =
    useMemo(() => {
      const start =
        page *
        rowsPerPage;

      return filteredDocuments.slice(
        start,
        start +
          rowsPerPage
      );
    }, [
      filteredDocuments,
      page,
      rowsPerPage,
    ]);


  useEffect(() => {
    setPage(0);
  }, [
    search,
    gradeFilter,
    subjectFilter,
    academicYearFilter,
    typeFilter,
    statusFilter,
  ]);


  const clearFilters = () => {
    setSearch("");
    setGradeFilter("");
    setSubjectFilter("");
    setAcademicYearFilter("");
    setTypeFilter("");
    setStatusFilter("");
    setPage(0);
  };


  const handleEdit = (document) => {
    setEditingDocument(
      document
    );
    setShowEditor(true);
  };


  const handleEditorSuccess = () => {
    setShowEditor(false);
    setEditingDocument(null);
    loadCurriculums(true);
  };


  const handleDelete = async (
    document
  ) => {
    const curriculumLabel =
      getCurriculumLabel(
        document
      );

    const result =
      await Swal.fire({
        icon: "warning",
        title: "Confirm Delete",
        html: `
          <div style="text-align:left">
            <strong>${curriculumLabel}</strong>
            <br />
            <span style="color:#6b7280">
              ${getGradeName(document)} •
              ${getSubjectName(document)}
            </span>
            <br />
            <span style="color:#6b7280">
              Academic Year:
              ${getAcademicYearName(document)}
            </span>
            <br />
            <span style="color:#6b7280">
              File:
              ${getFilename(document) || "N/A"}
            </span>
          </div>
          <br />
          <span>
            This action cannot be undone.
            Are you sure you want to delete this curriculum?
          </span>
        `,
        showCancelButton: true,
        confirmButtonText:
          "Yes, Delete Curriculum",
        cancelButtonText:
          "Cancel",
        confirmButtonColor:
          "#c62828",
        cancelButtonColor:
          "#6b7280",
        reverseButtons: true,
      });

    if (!result.isConfirmed) {
      return;
    }

    Swal.fire({
      title:
        "Deleting Curriculum",
      text:
        "Please wait...",
      allowOutsideClick:
        false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await api.delete(
        `/national-curriculum/${document.id}/`
      );

      setDocuments(
        (previous) =>
          previous.filter(
            (item) =>
              item.id !==
              document.id
          )
      );

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text:
          "The curriculum has been deleted successfully.",
        timer: 1800,
        showConfirmButton:
          false,
      });
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title:
          "Delete Failed",
        text:
          getErrorMessage(
            requestError
          ),
        confirmButtonColor:
          "#062A4B",
      });
    }
  };


  const handlePublishToggle =
    async (document) => {
      const action =
        document.is_published
          ? "unpublish"
          : "publish";

      const label =
        document.is_published
          ? "Unpublish"
          : "Publish";

      const curriculumLabel =
        getCurriculumLabel(
          document
        );

      const result =
        await Swal.fire({
          icon:
            action ===
            "publish"
              ? "question"
              : "warning",
          title:
            `${label} Curriculum?`,
          html: `
            <div style="text-align:left">
              <strong>${curriculumLabel}</strong>
              <br />
              <span style="color:#6b7280">
                ${getGradeName(document)} •
                ${getSubjectName(document)}
              </span>
              <br />
              <span style="color:#6b7280">
                Academic Year:
                ${getAcademicYearName(document)}
              </span>
            </div>
            <br />
            <span>
              ${
                document.is_published
                  ? "Teachers will no longer see this curriculum as an available published resource."
                  : "This curriculum will become available to authorized teachers."
              }
            </span>
          `,
          showCancelButton:
            true,
          confirmButtonText:
            `Yes, ${label}`,
          cancelButtonText:
            "Cancel",
          confirmButtonColor:
            action ===
            "publish"
              ? "#4FAE22"
              : "#C0750D",
          reverseButtons:
            true,
        });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await api.post(
          `/national-curriculum/${document.id}/${action}/`
        );

        setDocuments(
          (previous) =>
            previous.map(
              (item) =>
                item.id ===
                document.id
                  ? {
                      ...item,
                      is_published:
                        action ===
                        "publish",
                      is_active:
                        action ===
                        "publish"
                          ? true
                          : item.is_active,
                    }
                  : item
            )
        );

        Swal.fire({
          icon: "success",
          title:
            action ===
            "publish"
              ? "Published"
              : "Unpublished",
          text:
            action ===
            "publish"
              ? "Curriculum published successfully."
              : "Curriculum unpublished successfully.",
          timer: 1500,
          showConfirmButton:
            false,
        });
      } catch (requestError) {
        Swal.fire({
          icon: "error",
          title:
            `${label} Failed`,
          text:
            getErrorMessage(
              requestError
            ),
          confirmButtonColor:
            "#062A4B",
        });
      }
    };


  const handleActiveToggle =
    async (document) => {
      const action =
        document.is_active
          ? "deactivate"
          : "activate";

      const label =
        document.is_active
          ? "Deactivate"
          : "Activate";

      const curriculumLabel =
        getCurriculumLabel(
          document
        );

      if (
        document.is_active &&
        document.is_published
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "Unpublish First",
          html: `
            <strong>${curriculumLabel}</strong>
            <br />
            <br />
            A published curriculum must be unpublished before it can be deactivated.
          `,
          confirmButtonColor:
            "#062A4B",
        });

        return;
      }

      const result =
        await Swal.fire({
          icon: "question",
          title:
            `${label} Curriculum?`,
          html: `
            <div style="text-align:left">
              <strong>${curriculumLabel}</strong>
              <br />
              <span style="color:#6b7280">
                ${getGradeName(document)} •
                ${getSubjectName(document)}
              </span>
              <br />
              <span style="color:#6b7280">
                Academic Year:
                ${getAcademicYearName(document)}
              </span>
            </div>
            <br />
            <span>
              ${
                action ===
                "activate"
                  ? "This curriculum will become active."
                  : "This curriculum will become inactive."
              }
            </span>
          `,
          showCancelButton:
            true,
          confirmButtonText:
            `Yes, ${label}`,
          cancelButtonText:
            "Cancel",
          confirmButtonColor:
            action ===
            "activate"
              ? "#4FAE22"
              : "#C0750D",
          reverseButtons:
            true,
        });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await api.post(
          `/national-curriculum/${document.id}/${action}/`
        );

        setDocuments(
          (previous) =>
            previous.map(
              (item) =>
                item.id ===
                document.id
                  ? {
                      ...item,
                      is_active:
                        action ===
                        "activate",
                    }
                  : item
            )
        );

        Swal.fire({
          icon: "success",
          title:
            action ===
            "activate"
              ? "Activated"
              : "Deactivated",
          text:
            action ===
            "activate"
              ? "Curriculum activated successfully."
              : "Curriculum deactivated successfully.",
          timer: 1500,
          showConfirmButton:
            false,
        });
      } catch (requestError) {
        Swal.fire({
          icon: "error",
          title:
            `${label} Failed`,
          text:
            getErrorMessage(
              requestError
            ),
          confirmButtonColor:
            "#062A4B",
        });
      }
    };


  const statistics = useMemo(() => {
    const total =
      documents.length;

    const published =
      documents.filter(
        (item) =>
          item.is_published
      ).length;

    const active =
      documents.filter(
        (item) =>
          item.is_active
      ).length;

    const downloads =
      documents.reduce(
        (sum, item) =>
          sum +
          Number(
            item.download_count ||
              0
          ),
        0
      );

    return {
      total,
      published,
      active,
      downloads,
    };
  }, [documents]);


  if (showEditor) {
    return (
      <CurriculumPost
        editDocument={
          editingDocument
        }
        onBack={() => {
          setShowEditor(false);
          setEditingDocument(null);
        }}
        onSuccess={
          handleEditorSuccess
        }
      />
    );
  }


  return (
    <Box
      sx={{
        "--cayep-nav-navy":
          "#062A4B",
        "--cayep-nav-blue":
          "#063A6B",
        "--cayep-nav-black":
          "#0A0F12",
        "--cayep-nav-gold":
          "#F6C944",
        "--cayep-nav-yellow":
          "#EFA81C",
        "--cayep-nav-orange":
          "#C0750D",
        "--cayep-nav-green":
          "#4FAE22",
        "--cayep-nav-olive":
          "#5F6622",
        "--cayep-nav-sky":
          "#0F6DAB",
        "--cayep-nav-white":
          "#F0EDEA",
        "--cayep-nav-gray":
          "#C4C7C7",
        "--nav-white":
          "#ffffff",
        "--nav-text":
          "#142536",
        "--nav-muted":
          "#6b7280",
        "--nav-border":
          "#e7ebee",
        "--nav-soft":
          "#f7f9fa",
        "--nav-shadow":
          "0 12px 35px rgba(6, 42, 75, 0.10)",
        "--nav-transition":
          "220ms ease",

        minHeight:
          "100vh",

        background:
          "linear-gradient(135deg,#f7f9fa,#edf2f4)",

        p: {
          xs: 1.5,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            lg: "center",
          }}
          spacing={2}
          mb={3}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <Box
              sx={{
                width: 55,
                height: 55,
                borderRadius: 2.5,
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                color: "#fff",
                background:
                  "linear-gradient(135deg,#062A4B,#0F6DAB)",
                boxShadow:
                  "0 10px 25px rgba(6,42,75,.20)",
              }}
            >
              <LibraryBooks
                sx={{
                  fontSize: 31,
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="h4"
                fontWeight={900}
                color="#062A4B"
                sx={{
                  letterSpacing:
                    "-0.6px",
                }}
              >
                All Curriculum
              </Typography>

              <Typography
                color="#6b7280"
                variant="body2"
              >
                Complete curriculum
                database, publication
                and document management.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={
                <Refresh />
              }
              onClick={() =>
                loadCurriculums(
                  true
                )
              }
              disabled={
                refreshing
              }
              sx={{
                borderRadius: 2,
                borderColor:
                  "#062A4B",
                color:
                  "#062A4B",
                fontWeight: 800,
              }}
            >
              {refreshing
                ? "Refreshing..."
                : "Refresh"}
            </Button>

            <Button
              variant="contained"
              startIcon={
                <LibraryBooks />
              }
              onClick={() => {
                setEditingDocument(
                  null
                );
                setShowEditor(
                  true
                );
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 800,
                background:
                  "linear-gradient(135deg,#062A4B,#0F6DAB)",
              }}
            >
              Post Curriculum
            </Button>
          </Stack>
        </Stack>


        <Grid
          container
          spacing={2}
          mb={3}
        >
          {[
            {
              label: "Total",
              value:
                statistics.total,
              icon:
                <LibraryBooks />,
              color:
                "#062A4B",
            },
            {
              label: "Published",
              value:
                statistics.published,
              icon:
                <Publish />,
              color:
                "#4FAE22",
            },
            {
              label: "Active",
              value:
                statistics.active,
              icon:
                <CheckCircle />,
              color:
                "#0F6DAB",
            },
            {
              label: "Downloads",
              value:
                statistics.downloads,
              icon:
                <CloudDownload />,
              color:
                "#C0750D",
            },
          ].map(
            (item) => (
              <Grid
                item
                xs={6}
                md={3}
                key={
                  item.label
                }
              >
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow:
                      "0 12px 35px rgba(6,42,75,.08)",
                    border:
                      "1px solid rgba(6,42,75,.07)",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 45,
                          height: 45,
                          borderRadius: 2,
                          display:
                            "flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "center",
                          color: "#fff",
                          background:
                            item.color,
                        }}
                      >
                        {
                          item.icon
                        }
                      </Box>

                      <Box>
                        <Typography
                          variant="h5"
                          fontWeight={
                            900
                          }
                          color="#142536"
                        >
                          {
                            item.value
                          }
                        </Typography>

                        <Typography
                          variant="caption"
                          color="#6b7280"
                        >
                          {
                            item.label
                          }
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )
          )}
        </Grid>


        <Card
          sx={{
            borderRadius: 3,
            mb: 2,
            boxShadow:
              "0 12px 35px rgba(6,42,75,.08)",
          }}
        >
          <CardContent>
            <Grid
              container
              spacing={1.5}
            >
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  fullWidth
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target
                        .value
                    )
                  }
                  placeholder="Search curriculum, filename, subject, grade, code..."
                  InputProps={{
                    startAdornment:
                      (
                        <InputAdornment position="start">
                          <Search
                            sx={{
                              color:
                                "#0F6DAB",
                            }}
                          />
                        </InputAdornment>
                      ),
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                >
                  <InputLabel>
                    Grade / Class
                  </InputLabel>

                  <Select
                    value={
                      gradeFilter
                    }
                    label="Grade / Class"
                    onChange={(
                      event
                    ) =>
                      setGradeFilter(
                        event
                          .target
                          .value
                      )
                    }
                  >
                    <MenuItem value="">
                      All Grades
                    </MenuItem>

                    {gradeOptions.map(
                      (
                        grade
                      ) => (
                        <MenuItem
                          key={
                            grade
                          }
                          value={
                            grade
                          }
                        >
                          {
                            grade
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                >
                  <InputLabel>
                    Subject
                  </InputLabel>

                  <Select
                    value={
                      subjectFilter
                    }
                    label="Subject"
                    onChange={(
                      event
                    ) =>
                      setSubjectFilter(
                        event
                          .target
                          .value
                      )
                    }
                  >
                    <MenuItem value="">
                      All Subjects
                    </MenuItem>

                    {subjectOptions.map(
                      (
                        subject
                      ) => (
                        <MenuItem
                          key={
                            subject
                          }
                          value={
                            subject
                          }
                        >
                          {
                            subject
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                >
                  <InputLabel>
                    Academic Year
                  </InputLabel>

                  <Select
                    value={
                      academicYearFilter
                    }
                    label="Academic Year"
                    onChange={(
                      event
                    ) =>
                      setAcademicYearFilter(
                        event
                          .target
                          .value
                      )
                    }
                  >
                    <MenuItem value="">
                      All Years
                    </MenuItem>

                    {academicYearOptions.map(
                      (
                        year
                      ) => (
                        <MenuItem
                          key={
                            year
                          }
                          value={
                            year
                          }
                        >
                          {
                            year
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                >
                  <InputLabel>
                    Status
                  </InputLabel>

                  <Select
                    value={
                      statusFilter
                    }
                    label="Status"
                    onChange={(
                      event
                    ) =>
                      setStatusFilter(
                        event
                          .target
                          .value
                      )
                    }
                  >
                    <MenuItem value="">
                      All Status
                    </MenuItem>

                    <MenuItem value="published">
                      Published
                    </MenuItem>

                    <MenuItem value="draft">
                      Draft
                    </MenuItem>

                    <MenuItem value="active">
                      Active
                    </MenuItem>

                    <MenuItem value="inactive">
                      Inactive
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={2}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <FilterAlt />
                  }
                  onClick={
                    clearFilters
                  }
                  sx={{
                    height: 56,
                    borderRadius: 2,
                    color:
                      "#062A4B",
                    borderColor:
                      "#062A4B",
                    fontWeight: 800,
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>


        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              whiteSpace:
                "pre-line",
            }}
          >
            {error}
          </Alert>
        )}


        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow:
              "0 15px 40px rgba(6,42,75,.10)",
            border:
              "1px solid rgba(6,42,75,.08)",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              background:
                "linear-gradient(135deg,#062A4B,#063A6B)",
              color: "#fff",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography
                  fontWeight={900}
                  fontSize={18}
                >
                  Curriculum Database
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color:
                      "rgba(255,255,255,.70)",
                  }}
                >
                  Showing{" "}
                  {
                    filteredDocuments.length
                  }{" "}
                  of{" "}
                  {
                    documents.length
                  }{" "}
                  records
                </Typography>
              </Box>

              <CloudDownload
                sx={{
                  color:
                    "#F6C944",
                }}
              />
            </Stack>
          </Box>


          {loading ? (
            <Box
              sx={{
                minHeight: 350,
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
              }}
            >
              <Stack
                alignItems="center"
                spacing={2}
              >
                <CircularProgress
                  sx={{
                    color:
                      "#0F6DAB",
                  }}
                />

                <Typography
                  color="#6b7280"
                >
                  Loading curriculum
                  database...
                </Typography>
              </Stack>
            </Box>
          ) : (
            <>
              <TableContainer
                sx={{
                  maxHeight: 720,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                          minWidth:
                            300,
                        }}
                      >
                        Curriculum
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Academic Year
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Grade / Class
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Subject
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Type
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Version
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Status
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Downloads
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                        }}
                      >
                        Uploaded
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          fontWeight:
                            900,
                          color:
                            "#062A4B",
                          background:
                            "#f4f7f8",
                          minWidth:
                            180,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedDocuments.map(
                      (
                        document
                      ) => {
                        const curriculumLabel =
                          getCurriculumLabel(
                            document
                          );

                        const filename =
                          getFilename(
                            document
                          );

                        return (
                          <TableRow
                            key={
                              document.id
                            }
                            hover
                            sx={{
                              "&:hover":
                                {
                                  background:
                                    "#f8fafb",
                                },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={
                                  1.2
                                }
                                alignItems="center"
                              >
                                <Box
                                  sx={{
                                    width: 38,
                                    height: 44,
                                    flexShrink:
                                      0,
                                    borderRadius:
                                      "5px 7px 7px 5px",
                                    display:
                                      "flex",
                                    alignItems:
                                      "center",
                                    justifyContent:
                                      "center",
                                    color:
                                      "#F6C944",
                                    background:
                                      "linear-gradient(145deg,#062A4B,#0A0F12)",
                                    boxShadow:
                                      "0 4px 10px rgba(6,42,75,.15)",
                                  }}
                                >
                                  <Description
                                    fontSize="small"
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    minWidth:
                                      0,
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      filename ||
                                      curriculumLabel
                                    }
                                  >
                                    <Typography
                                      fontWeight={
                                        800
                                      }
                                      color="#142536"
                                      sx={{
                                        maxWidth:
                                          280,
                                        whiteSpace:
                                          "nowrap",
                                        overflow:
                                          "hidden",
                                        textOverflow:
                                          "ellipsis",
                                        cursor:
                                          "default",
                                      }}
                                    >
                                      {
                                        curriculumLabel
                                      }
                                    </Typography>
                                  </Tooltip>

                                  <Typography
                                    variant="caption"
                                    color="#6b7280"
                                    sx={{
                                      display:
                                        "block",
                                      maxWidth:
                                        280,
                                      whiteSpace:
                                        "nowrap",
                                      overflow:
                                        "hidden",
                                      textOverflow:
                                        "ellipsis",
                                    }}
                                    title={
                                      filename
                                    }
                                  >
                                    File:{" "}
                                    {
                                      filename ||
                                      "N/A"
                                    }
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="#6b7280"
                                    sx={{
                                      display:
                                        "block",
                                    }}
                                  >
                                    Code:{" "}
                                    {
                                      document.document_code ||
                                      "N/A"
                                    }
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={
                                  700
                                }
                              >
                                {getAcademicYearName(
                                  document
                                )}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                icon={
                                  <School />
                                }
                                label={getGradeName(
                                  document
                                )}
                                sx={{
                                  fontWeight:
                                    700,
                                  background:
                                    "rgba(15,109,171,.08)",
                                  color:
                                    "#0F5E92",
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                icon={
                                  <SubjectIcon />
                                }
                                label={getSubjectName(
                                  document
                                )}
                                sx={{
                                  fontWeight:
                                    700,
                                  background:
                                    "rgba(79,174,34,.08)",
                                  color:
                                    "#367c18",
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                              >
                                {
                                  getDocumentTypeName(
                                    document
                                  )
                                }
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  document.version ||
                                  "1.0"
                                }
                                sx={{
                                  fontWeight:
                                    800,
                                  background:
                                    "#f2f4f5",
                                  color:
                                    "#142536",
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Stack
                                spacing={
                                  0.5
                                }
                              >
                                <Chip
                                  size="small"
                                  label={
                                    document.is_published
                                      ? "Published"
                                      : "Draft"
                                  }
                                  sx={{
                                    width:
                                      "fit-content",
                                    fontWeight:
                                      800,
                                    background:
                                      document.is_published
                                        ? "rgba(79,174,34,.12)"
                                        : "rgba(192,117,13,.12)",
                                    color:
                                      document.is_published
                                        ? "#367c18"
                                        : "#995e0b",
                                  }}
                                />

                                <Chip
                                  size="small"
                                  label={
                                    document.is_active
                                      ? "Active"
                                      : "Inactive"
                                  }
                                  sx={{
                                    width:
                                      "fit-content",
                                    fontWeight:
                                      700,
                                    background:
                                      document.is_active
                                        ? "rgba(15,109,171,.08)"
                                        : "rgba(107,114,128,.10)",
                                    color:
                                      document.is_active
                                        ? "#0F5E92"
                                        : "#6b7280",
                                  }}
                                />
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Typography
                                fontWeight={
                                  800
                                }
                              >
                                {
                                  document.download_count ||
                                  0
                                }
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Tooltip
                                title={
                                  document.date_uploaded
                                    ? formatDateTime(
                                        document.date_uploaded
                                      )
                                    : ""
                                }
                              >
                                <Typography
                                  variant="body2"
                                  color="#53616c"
                                >
                                  {formatDate(
                                    document.date_uploaded
                                  )}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={
                                  0.5
                                }
                                justifyContent="flex-end"
                              >
                                <Tooltip title="Edit">
                                  <IconButton
                                    onClick={() =>
                                      handleEdit(
                                        document
                                      )
                                    }
                                    sx={{
                                      color:
                                        "#0F6DAB",
                                    }}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip
                                  title={
                                    document.is_published
                                      ? "Unpublish"
                                      : "Publish"
                                  }
                                >
                                  <IconButton
                                    onClick={() =>
                                      handlePublishToggle(
                                        document
                                      )
                                    }
                                    sx={{
                                      color:
                                        document.is_published
                                          ? "#C0750D"
                                          : "#4FAE22",
                                    }}
                                  >
                                    <Publish />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip
                                  title={
                                    document.is_active
                                      ? "Deactivate"
                                      : "Activate"
                                  }
                                >
                                  <IconButton
                                    onClick={() =>
                                      handleActiveToggle(
                                        document
                                      )
                                    }
                                    sx={{
                                      color:
                                        document.is_active
                                          ? "#5F6622"
                                          : "#4FAE22",
                                    }}
                                  >
                                    {document.is_active ? (
                                      <ToggleOn />
                                    ) : (
                                      <ToggleOff />
                                    )}
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete">
                                  <IconButton
                                    onClick={() =>
                                      handleDelete(
                                        document
                                      )
                                    }
                                    sx={{
                                      color:
                                        "#c62828",
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )}

                    {paginatedDocuments.length ===
                      0 && (
                      <TableRow>
                        <TableCell
                          colSpan={
                            10
                          }
                          align="center"
                          sx={{
                            py: 8,
                          }}
                        >
                          <LibraryBooks
                            sx={{
                              fontSize:
                                60,
                              color:
                                "#C4C7C7",
                            }}
                          />

                          <Typography
                            variant="h6"
                            fontWeight={
                              800
                            }
                            color="#062A4B"
                          >
                            No Curriculum
                            Records
                          </Typography>

                          <Typography
                            color="#6b7280"
                          >
                            No curriculum
                            matches your
                            current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              <TablePagination
                component="div"
                count={
                  filteredDocuments.length
                }
                page={
                  page
                }
                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPage(
                    newPage
                  )
                }
                rowsPerPage={
                  rowsPerPage
                }
                onRowsPerPageChange={(
                  event
                ) => {
                  setRowsPerPage(
                    parseInt(
                      event.target
                        .value,
                      10
                    )
                  );
                  setPage(
                    0
                  );
                }}
                rowsPerPageOptions={[
                  10,
                  25,
                  50,
                  100,
                ]}
              />
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
};


export default AllCurriculumLis;



// import React, {
//   useEffect,
//   useMemo,
//   useState,
// } from "react";

// import api from "../../api/api";
// import Swal from "sweetalert2";

// import {
//   Alert,
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Chip,
//   CircularProgress,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Divider,
//   FormControl,
//   Grid,
//   IconButton,
//   InputAdornment,
//   InputLabel,
//   MenuItem,
//   Paper,
//   Select,
//   Stack,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TablePagination,
//   TableRow,
//   TextField,
//   Tooltip,
//   Typography,
// } from "@mui/material";

// import {
//   CheckCircle,
//   Delete,
//   Description,
//   Edit,
//   FilterAlt,
//   LibraryBooks,
//   MoreHoriz,
//   Publish,
//   Refresh,
//   Search,
//   School,
//   Subject as SubjectIcon,
//   ToggleOff,
//   ToggleOn,
//   Visibility,
//   CloudDownload,
// } from "@mui/icons-material";

// import CurriculumPost from "./CurriculumPost";


// const DOCUMENT_TYPES = {
//   curriculum: "National Curriculum",
//   syllabus: "Syllabus",
//   teacher_guide: "Teacher Guide",
//   framework: "Curriculum Framework",
//   assessment: "Assessment Guide",
//   other: "Other",
// };


// const getErrorMessage = (error) => {
//   const data = error?.response?.data;

//   if (!data) {
//     return (
//       error?.message ||
//       "An unexpected error occurred."
//     );
//   }

//   if (typeof data === "string") {
//     return data;
//   }

//   if (data.detail) {
//     return data.detail;
//   }

//   if (typeof data === "object") {
//     return Object.entries(data)
//       .map(([key, value]) => {
//         if (Array.isArray(value)) {
//           return `${key}: ${value.join(", ")}`;
//         }

//         return `${key}: ${value}`;
//       })
//       .join("\n");
//   }

//   return "Unable to complete the request.";
// };


// const normalizeItems = (response) => {
//   const data = response?.data;

//   if (Array.isArray(data)) {
//     return data;
//   }

//   if (Array.isArray(data?.results)) {
//     return data.results;
//   }

//   return [];
// };


// const getSubjectName = (document) =>
//   document?.subject_name ||
//   document?.subject_data?.name ||
//   document?.subject?.name ||
//   "General";


// const getGradeName = (document) =>
//   document?.grade_class_name ||
//   document?.grade_class_data?.name ||
//   document?.grade_class?.name ||
//   "—";


// const getAcademicYearName = (document) =>
//   document?.academic_year_name ||
//   document?.academic_year_data?.name ||
//   document?.academic_year_data?.year ||
//   document?.academic_year ||
//   "—";


// const formatDate = (value) => {
//   if (!value) {
//     return "—";
//   }

//   const date = new Date(value);

//   if (Number.isNaN(date.getTime())) {
//     return value;
//   }

//   return date.toLocaleDateString(
//     undefined,
//     {
//       year: "numeric",
//       month: "short",
//       day: "numeric",
//     }
//   );
// };


// const AllCurriculumLis = () => {
//   const [documents, setDocuments] =
//     useState([]);

//   const [loading, setLoading] =
//     useState(true);

//   const [refreshing, setRefreshing] =
//     useState(false);

//   const [error, setError] =
//     useState("");

//   const [search, setSearch] =
//     useState("");

//   const [gradeFilter, setGradeFilter] =
//     useState("");

//   const [subjectFilter, setSubjectFilter] =
//     useState("");

//   const [academicYearFilter, setAcademicYearFilter] =
//     useState("");

//   const [statusFilter, setStatusFilter] =
//     useState("");

//   const [typeFilter, setTypeFilter] =
//     useState("");

//   const [page, setPage] =
//     useState(0);

//   const [rowsPerPage, setRowsPerPage] =
//     useState(10);

//   const [editingDocument, setEditingDocument] =
//     useState(null);

//   const [showEditor, setShowEditor] =
//     useState(false);


//   const loadCurriculums = async (
//     showRefresh = false
//   ) => {
//     if (showRefresh) {
//       setRefreshing(true);
//     } else {
//       setLoading(true);
//     }

//     setError("");

//     try {
//       const response = await api.get(
//         "/national-curriculum/",
//         {
//           params: {
//             page_size: 1000,
//           },
//         }
//       );

//       setDocuments(
//         normalizeItems(response)
//       );
//     } catch (requestError) {
//       setError(
//         getErrorMessage(
//           requestError
//         )
//       );
//     } finally {
//       setLoading(false);
//       setRefreshing(false);
//     }
//   };


//   useEffect(() => {
//     loadCurriculums();
//   }, []);


//   const gradeOptions = useMemo(() => {
//     return [
//       ...new Set(
//         documents
//           .map(getGradeName)
//           .filter(Boolean)
//       ),
//     ].sort();
//   }, [documents]);


//   const subjectOptions = useMemo(() => {
//     return [
//       ...new Set(
//         documents
//           .map(getSubjectName)
//           .filter(Boolean)
//       ),
//     ].sort();
//   }, [documents]);


//   const academicYearOptions =
//     useMemo(() => {
//       return [
//         ...new Set(
//           documents
//             .map(
//               getAcademicYearName
//             )
//             .filter(Boolean)
//         ),
//       ].sort();
//     }, [documents]);


//   const filteredDocuments = useMemo(() => {
//     const query =
//       search.trim().toLowerCase();

//     return documents.filter(
//       (document) => {
//         const matchesSearch =
//           !query ||
//           document.title
//             ?.toLowerCase()
//             .includes(query) ||
//           document.document_code
//             ?.toLowerCase()
//             .includes(query) ||
//           document.description
//             ?.toLowerCase()
//             .includes(query) ||
//           getSubjectName(
//             document
//           )
//             .toLowerCase()
//             .includes(query) ||
//           getGradeName(
//             document
//           )
//             .toLowerCase()
//             .includes(query);

//         const matchesGrade =
//           !gradeFilter ||
//           getGradeName(document) ===
//             gradeFilter;

//         const matchesSubject =
//           !subjectFilter ||
//           getSubjectName(document) ===
//             subjectFilter;

//         const matchesAcademicYear =
//           !academicYearFilter ||
//           getAcademicYearName(
//             document
//           ) === academicYearFilter;

//         const matchesType =
//           !typeFilter ||
//           document.document_type ===
//             typeFilter;

//         let matchesStatus = true;

//         if (statusFilter === "published") {
//           matchesStatus =
//             document.is_published === true;
//         }

//         if (statusFilter === "draft") {
//           matchesStatus =
//             document.is_published === false;
//         }

//         if (statusFilter === "active") {
//           matchesStatus =
//             document.is_active === true;
//         }

//         if (statusFilter === "inactive") {
//           matchesStatus =
//             document.is_active === false;
//         }

//         return (
//           matchesSearch &&
//           matchesGrade &&
//           matchesSubject &&
//           matchesAcademicYear &&
//           matchesType &&
//           matchesStatus
//         );
//       }
//     );
//   }, [
//     documents,
//     search,
//     gradeFilter,
//     subjectFilter,
//     academicYearFilter,
//     typeFilter,
//     statusFilter,
//   ]);


//   const paginatedDocuments =
//     useMemo(() => {
//       const start =
//         page * rowsPerPage;

//       return filteredDocuments.slice(
//         start,
//         start + rowsPerPage
//       );
//     }, [
//       filteredDocuments,
//       page,
//       rowsPerPage,
//     ]);


//   useEffect(() => {
//     setPage(0);
//   }, [
//     search,
//     gradeFilter,
//     subjectFilter,
//     academicYearFilter,
//     typeFilter,
//     statusFilter,
//   ]);


//   const clearFilters = () => {
//     setSearch("");
//     setGradeFilter("");
//     setSubjectFilter("");
//     setAcademicYearFilter("");
//     setTypeFilter("");
//     setStatusFilter("");
//     setPage(0);
//   };


//   const handleEdit = (document) => {
//     setEditingDocument(document);
//     setShowEditor(true);
//   };


//   const handleEditorSuccess = () => {
//     setShowEditor(false);
//     setEditingDocument(null);
//     loadCurriculums(true);
//   };


//   const handleDelete = async (
//     document
//   ) => {
//     const result = await Swal.fire({
//       icon: "warning",
//       title: "Confirm Delete",
//       html: `
//         <div style="text-align:left">
//           <strong>${document.title}</strong>
//           <br />
//           <span style="color:#6b7280">
//             ${getGradeName(document)} •
//             ${getSubjectName(document)}
//           </span>
//           <br />
//           <span style="color:#6b7280">
//             Academic Year:
//             ${getAcademicYearName(document)}
//           </span>
//         </div>
//         <br />
//         <span>
//           This action cannot be undone.
//           Are you sure you want to delete this curriculum?
//         </span>
//       `,
//       showCancelButton: true,
//       confirmButtonText:
//         "Yes, Delete Curriculum",
//       cancelButtonText:
//         "Cancel",
//       confirmButtonColor: "#c62828",
//       cancelButtonColor: "#6b7280",
//       reverseButtons: true,
//     });

//     if (!result.isConfirmed) {
//       return;
//     }

//     Swal.fire({
//       title: "Deleting Curriculum",
//       text: "Please wait...",
//       allowOutsideClick: false,
//       didOpen: () => {
//         Swal.showLoading();
//       },
//     });

//     try {
//       await api.delete(
//         `/national-curriculum/${document.id}/`
//       );

//       setDocuments((previous) =>
//         previous.filter(
//           (item) =>
//             item.id !== document.id
//         )
//       );

//       Swal.fire({
//         icon: "success",
//         title: "Deleted",
//         text: "The curriculum has been deleted successfully.",
//         timer: 1800,
//         showConfirmButton: false,
//       });
//     } catch (requestError) {
//       Swal.fire({
//         icon: "error",
//         title: "Delete Failed",
//         text: getErrorMessage(
//           requestError
//         ),
//         confirmButtonColor: "#062A4B",
//       });
//     }
//   };


//   const handlePublishToggle = async (
//     document
//   ) => {
//     const action =
//       document.is_published
//         ? "unpublish"
//         : "publish";

//     const label =
//       document.is_published
//         ? "Unpublish"
//         : "Publish";

//     const result = await Swal.fire({
//       icon:
//         action === "publish"
//           ? "question"
//           : "warning",
//       title: `${label} Curriculum?`,
//       text: document.is_published
//         ? "Teachers will no longer see this curriculum as an available published resource."
//         : "This curriculum will become available to authorized teachers.",
//       showCancelButton: true,
//       confirmButtonText:
//         `Yes, ${label}`,
//       cancelButtonText:
//         "Cancel",
//       confirmButtonColor:
//         action === "publish"
//           ? "#4FAE22"
//           : "#C0750D",
//       reverseButtons: true,
//     });

//     if (!result.isConfirmed) {
//       return;
//     }

//     try {
//       await api.post(
//         `/national-curriculum/${document.id}/${action}/`
//       );

//       setDocuments((previous) =>
//         previous.map((item) =>
//           item.id === document.id
//             ? {
//                 ...item,
//                 is_published:
//                   action === "publish",
//                 is_active:
//                   action === "publish"
//                     ? true
//                     : item.is_active,
//               }
//             : item
//         )
//       );

//       Swal.fire({
//         icon: "success",
//         title: `${label}ed`,
//         text: `Curriculum ${label.toLowerCase()}ed successfully.`,
//         timer: 1500,
//         showConfirmButton: false,
//       });
//     } catch (requestError) {
//       Swal.fire({
//         icon: "error",
//         title: `${label} Failed`,
//         text: getErrorMessage(
//           requestError
//         ),
//         confirmButtonColor: "#062A4B",
//       });
//     }
//   };


//   const handleActiveToggle = async (
//     document
//   ) => {
//     const action =
//       document.is_active
//         ? "deactivate"
//         : "activate";

//     const label =
//       document.is_active
//         ? "Deactivate"
//         : "Activate";

//     if (
//       document.is_active &&
//       document.is_published
//     ) {
//       Swal.fire({
//         icon: "warning",
//         title: "Unpublish First",
//         text:
//           "A published curriculum must be unpublished before it can be deactivated.",
//         confirmButtonColor: "#062A4B",
//       });

//       return;
//     }

//     const result = await Swal.fire({
//       icon: "question",
//       title: `${label} Curriculum?`,
//       text:
//         action === "activate"
//           ? "This curriculum will become active."
//           : "This curriculum will become inactive.",
//       showCancelButton: true,
//       confirmButtonText:
//         `Yes, ${label}`,
//       cancelButtonText:
//         "Cancel",
//       confirmButtonColor:
//         action === "activate"
//           ? "#4FAE22"
//           : "#C0750D",
//       reverseButtons: true,
//     });

//     if (!result.isConfirmed) {
//       return;
//     }

//     try {
//       await api.post(
//         `/national-curriculum/${document.id}/${action}/`
//       );

//       setDocuments((previous) =>
//         previous.map((item) =>
//           item.id === document.id
//             ? {
//                 ...item,
//                 is_active:
//                   action === "activate",
//               }
//             : item
//         )
//       );

//       Swal.fire({
//         icon: "success",
//         title: `${label}d`,
//         text: `Curriculum ${label.toLowerCase()}d successfully.`,
//         timer: 1500,
//         showConfirmButton: false,
//       });
//     } catch (requestError) {
//       Swal.fire({
//         icon: "error",
//         title: `${label} Failed`,
//         text: getErrorMessage(
//           requestError
//         ),
//         confirmButtonColor: "#062A4B",
//       });
//     }
//   };


//   const statistics = useMemo(() => {
//     const total =
//       documents.length;

//     const published =
//       documents.filter(
//         (item) =>
//           item.is_published
//       ).length;

//     const active =
//       documents.filter(
//         (item) =>
//           item.is_active
//       ).length;

//     const downloads =
//       documents.reduce(
//         (sum, item) =>
//           sum +
//           Number(
//             item.download_count || 0
//           ),
//         0
//       );

//     return {
//       total,
//       published,
//       active,
//       downloads,
//     };
//   }, [documents]);


//   if (showEditor) {
//     return (
//       <CurriculumPost
//         editDocument={
//           editingDocument
//         }
//         onBack={() => {
//           setShowEditor(false);
//           setEditingDocument(null);
//         }}
//         onSuccess={
//           handleEditorSuccess
//         }
//       />
//     );
//   }


//   return (
//     <Box
//       sx={{
//         "--cayep-nav-navy": "#062A4B",
//         "--cayep-nav-blue": "#063A6B",
//         "--cayep-nav-black": "#0A0F12",
//         "--cayep-nav-gold": "#F6C944",
//         "--cayep-nav-yellow": "#EFA81C",
//         "--cayep-nav-orange": "#C0750D",
//         "--cayep-nav-green": "#4FAE22",
//         "--cayep-nav-olive": "#5F6622",
//         "--cayep-nav-sky": "#0F6DAB",
//         "--cayep-nav-white": "#F0EDEA",
//         "--cayep-nav-gray": "#C4C7C7",
//         "--nav-white": "#ffffff",
//         "--nav-text": "#142536",
//         "--nav-muted": "#6b7280",
//         "--nav-border": "#e7ebee",
//         "--nav-soft": "#f7f9fa",
//         "--nav-shadow":
//           "0 12px 35px rgba(6, 42, 75, 0.10)",
//         "--nav-transition": "220ms ease",

//         minHeight: "100vh",
//         background:
//           "linear-gradient(135deg,#f7f9fa,#edf2f4)",
//         p: {
//           xs: 1.5,
//           md: 3,
//         },
//       }}
//     >
//       <Box
//         sx={{
//           maxWidth: 1600,
//           mx: "auto",
//         }}
//       >
//         <Stack
//           direction={{
//             xs: "column",
//             lg: "row",
//           }}
//           justifyContent="space-between"
//           alignItems={{
//             xs: "stretch",
//             lg: "center",
//           }}
//           spacing={2}
//           mb={3}
//         >
//           <Stack
//             direction="row"
//             spacing={1.5}
//             alignItems="center"
//           >
//             <Box
//               sx={{
//                 width: 55,
//                 height: 55,
//                 borderRadius: 2.5,
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 color: "#fff",
//                 background:
//                   "linear-gradient(135deg,#062A4B,#0F6DAB)",
//                 boxShadow:
//                   "0 10px 25px rgba(6,42,75,.20)",
//               }}
//             >
//               <LibraryBooks
//                 sx={{
//                   fontSize: 31,
//                 }}
//               />
//             </Box>

//             <Box>
//               <Typography
//                 variant="h4"
//                 fontWeight={900}
//                 color="#062A4B"
//                 sx={{
//                   letterSpacing:
//                     "-0.6px",
//                 }}
//               >
//                 All Curriculum
//               </Typography>

//               <Typography
//                 color="#6b7280"
//                 variant="body2"
//               >
//                 Complete curriculum database,
//                 publication and document management.
//               </Typography>
//             </Box>
//           </Stack>

//           <Stack
//             direction="row"
//             spacing={1}
//           >
//             <Button
//               variant="outlined"
//               startIcon={<Refresh />}
//               onClick={() =>
//                 loadCurriculums(true)
//               }
//               disabled={refreshing}
//               sx={{
//                 borderRadius: 2,
//                 borderColor: "#062A4B",
//                 color: "#062A4B",
//                 fontWeight: 800,
//               }}
//             >
//               {refreshing
//                 ? "Refreshing..."
//                 : "Refresh"}
//             </Button>

//             <Button
//               variant="contained"
//               startIcon={<LibraryBooks />}
//               onClick={() => {
//                 setEditingDocument(null);
//                 setShowEditor(true);
//               }}
//               sx={{
//                 borderRadius: 2,
//                 fontWeight: 800,
//                 background:
//                   "linear-gradient(135deg,#062A4B,#0F6DAB)",
//               }}
//             >
//               Post Curriculum
//             </Button>
//           </Stack>
//         </Stack>


//         <Grid
//           container
//           spacing={2}
//           mb={3}
//         >
//           {[
//             {
//               label: "Total",
//               value: statistics.total,
//               icon: <LibraryBooks />,
//               color: "#062A4B",
//             },
//             {
//               label: "Published",
//               value:
//                 statistics.published,
//               icon: <Publish />,
//               color: "#4FAE22",
//             },
//             {
//               label: "Active",
//               value:
//                 statistics.active,
//               icon: <CheckCircle />,
//               color: "#0F6DAB",
//             },
//             {
//               label: "Downloads",
//               value:
//                 statistics.downloads,
//               icon: <CloudDownload />,
//               color: "#C0750D",
//             },
//           ].map((item) => (
//             <Grid
//               item
//               xs={6}
//               md={3}
//               key={item.label}
//             >
//               <Card
//                 sx={{
//                   borderRadius: 3,
//                   boxShadow:
//                     "0 12px 35px rgba(6,42,75,.08)",
//                   border:
//                     "1px solid rgba(6,42,75,.07)",
//                 }}
//               >
//                 <CardContent>
//                   <Stack
//                     direction="row"
//                     spacing={1.5}
//                     alignItems="center"
//                   >
//                     <Box
//                       sx={{
//                         width: 45,
//                         height: 45,
//                         borderRadius: 2,
//                         display: "flex",
//                         alignItems:
//                           "center",
//                         justifyContent:
//                           "center",
//                         color: "#fff",
//                         background:
//                           item.color,
//                       }}
//                     >
//                       {item.icon}
//                     </Box>

//                     <Box>
//                       <Typography
//                         variant="h5"
//                         fontWeight={900}
//                         color="#142536"
//                       >
//                         {item.value}
//                       </Typography>

//                       <Typography
//                         variant="caption"
//                         color="#6b7280"
//                       >
//                         {item.label}
//                       </Typography>
//                     </Box>
//                   </Stack>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>


//         <Card
//           sx={{
//             borderRadius: 3,
//             mb: 2,
//             boxShadow:
//               "0 12px 35px rgba(6,42,75,.08)",
//           }}
//         >
//           <CardContent>
//             <Grid
//               container
//               spacing={1.5}
//             >
//               <Grid
//                 item
//                 xs={12}
//                 md={4}
//               >
//                 <TextField
//                   fullWidth
//                   value={search}
//                   onChange={(event) =>
//                     setSearch(
//                       event.target.value
//                     )
//                   }
//                   placeholder="Search title, subject, grade, code..."
//                   InputProps={{
//                     startAdornment: (
//                       <InputAdornment position="start">
//                         <Search
//                           sx={{
//                             color:
//                               "#0F6DAB",
//                           }}
//                         />
//                       </InputAdornment>
//                     ),
//                   }}
//                 />
//               </Grid>

//               <Grid
//                 item
//                 xs={12}
//                 sm={6}
//                 md={2}
//               >
//                 <FormControl fullWidth>
//                   <InputLabel>
//                     Grade / Class
//                   </InputLabel>

//                   <Select
//                     value={gradeFilter}
//                     label="Grade / Class"
//                     onChange={(event) =>
//                       setGradeFilter(
//                         event.target.value
//                       )
//                     }
//                   >
//                     <MenuItem value="">
//                       All Grades
//                     </MenuItem>

//                     {gradeOptions.map(
//                       (grade) => (
//                         <MenuItem
//                           key={grade}
//                           value={grade}
//                         >
//                           {grade}
//                         </MenuItem>
//                       )
//                     )}
//                   </Select>
//                 </FormControl>
//               </Grid>

//               <Grid
//                 item
//                 xs={12}
//                 sm={6}
//                 md={2}
//               >
//                 <FormControl fullWidth>
//                   <InputLabel>
//                     Subject
//                   </InputLabel>

//                   <Select
//                     value={subjectFilter}
//                     label="Subject"
//                     onChange={(event) =>
//                       setSubjectFilter(
//                         event.target.value
//                       )
//                     }
//                   >
//                     <MenuItem value="">
//                       All Subjects
//                     </MenuItem>

//                     {subjectOptions.map(
//                       (subject) => (
//                         <MenuItem
//                           key={subject}
//                           value={subject}
//                         >
//                           {subject}
//                         </MenuItem>
//                       )
//                     )}
//                   </Select>
//                 </FormControl>
//               </Grid>

//               <Grid
//                 item
//                 xs={12}
//                 sm={6}
//                 md={2}
//               >
//                 <FormControl fullWidth>
//                   <InputLabel>
//                     Academic Year
//                   </InputLabel>

//                   <Select
//                     value={
//                       academicYearFilter
//                     }
//                     label="Academic Year"
//                     onChange={(event) =>
//                       setAcademicYearFilter(
//                         event.target.value
//                       )
//                     }
//                   >
//                     <MenuItem value="">
//                       All Years
//                     </MenuItem>

//                     {academicYearOptions.map(
//                       (year) => (
//                         <MenuItem
//                           key={year}
//                           value={year}
//                         >
//                           {year}
//                         </MenuItem>
//                       )
//                     )}
//                   </Select>
//                 </FormControl>
//               </Grid>

//               <Grid
//                 item
//                 xs={12}
//                 sm={6}
//                 md={2}
//               >
//                 <FormControl fullWidth>
//                   <InputLabel>
//                     Status
//                   </InputLabel>

//                   <Select
//                     value={statusFilter}
//                     label="Status"
//                     onChange={(event) =>
//                       setStatusFilter(
//                         event.target.value
//                       )
//                     }
//                   >
//                     <MenuItem value="">
//                       All Status
//                     </MenuItem>

//                     <MenuItem value="published">
//                       Published
//                     </MenuItem>

//                     <MenuItem value="draft">
//                       Draft
//                     </MenuItem>

//                     <MenuItem value="active">
//                       Active
//                     </MenuItem>

//                     <MenuItem value="inactive">
//                       Inactive
//                     </MenuItem>
//                   </Select>
//                 </FormControl>
//               </Grid>

//               <Grid
//                 item
//                 xs={12}
//                 md={2}
//               >
//                 <Button
//                   fullWidth
//                   variant="outlined"
//                   startIcon={<FilterAlt />}
//                   onClick={clearFilters}
//                   sx={{
//                     height: 56,
//                     borderRadius: 2,
//                     color: "#062A4B",
//                     borderColor:
//                       "#062A4B",
//                     fontWeight: 800,
//                   }}
//                 >
//                   Clear Filters
//                 </Button>
//               </Grid>
//             </Grid>
//           </CardContent>
//         </Card>


//         {error && (
//           <Alert
//             severity="error"
//             sx={{
//               mb: 2,
//               borderRadius: 2,
//             }}
//           >
//             {error}
//           </Alert>
//         )}


//         <Card
//           sx={{
//             borderRadius: 3,
//             overflow: "hidden",
//             boxShadow:
//               "0 15px 40px rgba(6,42,75,.10)",
//             border:
//               "1px solid rgba(6,42,75,.08)",
//           }}
//         >
//           <Box
//             sx={{
//               px: 2.5,
//               py: 2,
//               background:
//                 "linear-gradient(135deg,#062A4B,#063A6B)",
//               color: "#fff",
//             }}
//           >
//             <Stack
//               direction="row"
//               justifyContent="space-between"
//               alignItems="center"
//             >
//               <Box>
//                 <Typography
//                   fontWeight={900}
//                   fontSize={18}
//                 >
//                   Curriculum Database
//                 </Typography>

//                 <Typography
//                   variant="caption"
//                   sx={{
//                     color:
//                       "rgba(255,255,255,.70)",
//                   }}
//                 >
//                   Showing{" "}
//                   {filteredDocuments.length}{" "}
//                   of {documents.length} records
//                 </Typography>
//               </Box>

//               <Description
//                 sx={{
//                   color: "#F6C944",
//                 }}
//               />
//             </Stack>
//           </Box>


//           {loading ? (
//             <Box
//               sx={{
//                 minHeight: 350,
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//               }}
//             >
//               <Stack
//                 alignItems="center"
//                 spacing={2}
//               >
//                 <CircularProgress
//                   sx={{
//                     color: "#0F6DAB",
//                   }}
//                 />

//                 <Typography
//                   color="#6b7280"
//                 >
//                   Loading curriculum database...
//                 </Typography>
//               </Stack>
//             </Box>
//           ) : (
//             <>
//               <TableContainer
//                 sx={{
//                   maxHeight: 720,
//                 }}
//               >
//                 <Table
//                   stickyHeader
//                   size="small"
//                 >
//                   <TableHead>
//                     <TableRow>
//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                           minWidth: 260,
//                         }}
//                       >
//                         Curriculum
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Academic Year
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Grade / Class
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Subject
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Type
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Version
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Status
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Downloads
//                       </TableCell>

//                       <TableCell
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                         }}
//                       >
//                         Uploaded
//                       </TableCell>

//                       <TableCell
//                         align="right"
//                         sx={{
//                           fontWeight: 900,
//                           color: "#062A4B",
//                           background:
//                             "#f4f7f8",
//                           minWidth: 170,
//                         }}
//                       >
//                         Actions
//                       </TableCell>
//                     </TableRow>
//                   </TableHead>

//                   <TableBody>
//                     {paginatedDocuments.map(
//                       (document) => (
//                         <TableRow
//                           key={document.id}
//                           hover
//                           sx={{
//                             "&:hover": {
//                               background:
//                                 "#f8fafb",
//                             },
//                           }}
//                         >
//                           <TableCell>
//                             <Stack
//                               direction="row"
//                               spacing={1.2}
//                               alignItems="center"
//                             >
//                               <Box
//                                 sx={{
//                                   width: 38,
//                                   height: 44,
//                                   borderRadius:
//                                     "5px 7px 7px 5px",
//                                   display:
//                                     "flex",
//                                   alignItems:
//                                     "center",
//                                   justifyContent:
//                                     "center",
//                                   color:
//                                     "#F6C944",
//                                   background:
//                                     "linear-gradient(145deg,#062A4B,#0A0F12)",
//                                   boxShadow:
//                                     "0 4px 10px rgba(6,42,75,.15)",
//                                 }}
//                               >
//                                 <Description
//                                   fontSize="small"
//                                 />
//                               </Box>

//                               <Box
//                                 sx={{
//                                   minWidth: 0,
//                                 }}
//                               >
//                                 <Typography
//                                   fontWeight={800}
//                                   color="#142536"
//                                   sx={{
//                                     maxWidth: 260,
//                                     whiteSpace:
//                                       "nowrap",
//                                     overflow:
//                                       "hidden",
//                                     textOverflow:
//                                       "ellipsis",
//                                   }}
//                                 >
//                                   {
//                                     document.title
//                                   }
//                                 </Typography>

//                                 <Typography
//                                   variant="caption"
//                                   color="#6b7280"
//                                 >
//                                   Code:{" "}
//                                   {document.document_code ||
//                                     "N/A"}
//                                 </Typography>
//                               </Box>
//                             </Stack>
//                           </TableCell>

//                           <TableCell>
//                             <Typography
//                               variant="body2"
//                               fontWeight={700}
//                             >
//                               {getAcademicYearName(
//                                 document
//                               )}
//                             </Typography>
//                           </TableCell>

//                           <TableCell>
//                             <Chip
//                               size="small"
//                               icon={
//                                 <School />
//                               }
//                               label={getGradeName(
//                                 document
//                               )}
//                               sx={{
//                                 fontWeight: 700,
//                                 background:
//                                   "rgba(15,109,171,.08)",
//                                 color:
//                                   "#0F5E92",
//                               }}
//                             />
//                           </TableCell>

//                           <TableCell>
//                             <Chip
//                               size="small"
//                               icon={
//                                 <SubjectIcon />
//                               }
//                               label={getSubjectName(
//                                 document
//                               )}
//                               sx={{
//                                 fontWeight: 700,
//                                 background:
//                                   "rgba(79,174,34,.08)",
//                                 color:
//                                   "#367c18",
//                               }}
//                             />
//                           </TableCell>

//                           <TableCell>
//                             <Typography
//                               variant="body2"
//                             >
//                               {
//                                 DOCUMENT_TYPES[
//                                   document
//                                     .document_type
//                                 ]
//                               }
//                             </Typography>
//                           </TableCell>

//                           <TableCell>
//                             <Chip
//                               size="small"
//                               label={
//                                 document.version ||
//                                 "1.0"
//                               }
//                               sx={{
//                                 fontWeight: 800,
//                                 background:
//                                   "#f2f4f5",
//                                 color:
//                                   "#142536",
//                               }}
//                             />
//                           </TableCell>

//                           <TableCell>
//                             <Stack
//                               spacing={0.5}
//                             >
//                               <Chip
//                                 size="small"
//                                 label={
//                                   document.is_published
//                                     ? "Published"
//                                     : "Draft"
//                                 }
//                                 sx={{
//                                   width:
//                                     "fit-content",
//                                   fontWeight: 800,
//                                   background:
//                                     document.is_published
//                                       ? "rgba(79,174,34,.12)"
//                                       : "rgba(192,117,13,.12)",
//                                   color:
//                                     document.is_published
//                                       ? "#367c18"
//                                       : "#995e0b",
//                                 }}
//                               />

//                               <Chip
//                                 size="small"
//                                 label={
//                                   document.is_active
//                                     ? "Active"
//                                     : "Inactive"
//                                 }
//                                 sx={{
//                                   width:
//                                     "fit-content",
//                                   fontWeight: 700,
//                                   background:
//                                     document.is_active
//                                       ? "rgba(15,109,171,.08)"
//                                       : "rgba(107,114,128,.10)",
//                                   color:
//                                     document.is_active
//                                       ? "#0F5E92"
//                                       : "#6b7280",
//                                 }}
//                               />
//                             </Stack>
//                           </TableCell>

//                           <TableCell>
//                             <Typography
//                               fontWeight={800}
//                             >
//                               {document.download_count ||
//                                 0}
//                             </Typography>
//                           </TableCell>

//                           <TableCell>
//                             <Typography
//                               variant="body2"
//                               color="#53616c"
//                             >
//                               {formatDate(
//                                 document.date_uploaded
//                               )}
//                             </Typography>
//                           </TableCell>

//                           <TableCell align="right">
//                             <Stack
//                               direction="row"
//                               spacing={0.5}
//                               justifyContent="flex-end"
//                             >
//                               <Tooltip title="Edit">
//                                 <IconButton
//                                   onClick={() =>
//                                     handleEdit(
//                                       document
//                                     )
//                                   }
//                                   sx={{
//                                     color:
//                                       "#0F6DAB",
//                                   }}
//                                 >
//                                   <Edit />
//                                 </IconButton>
//                               </Tooltip>

//                               <Tooltip
//                                 title={
//                                   document.is_published
//                                     ? "Unpublish"
//                                     : "Publish"
//                                 }
//                               >
//                                 <IconButton
//                                   onClick={() =>
//                                     handlePublishToggle(
//                                       document
//                                     )
//                                   }
//                                   sx={{
//                                     color:
//                                       document.is_published
//                                         ? "#C0750D"
//                                         : "#4FAE22",
//                                   }}
//                                 >
//                                   <Publish />
//                                 </IconButton>
//                               </Tooltip>

//                               <Tooltip
//                                 title={
//                                   document.is_active
//                                     ? "Deactivate"
//                                     : "Activate"
//                                 }
//                               >
//                                 <IconButton
//                                   onClick={() =>
//                                     handleActiveToggle(
//                                       document
//                                     )
//                                   }
//                                   sx={{
//                                     color:
//                                       document.is_active
//                                         ? "#5F6622"
//                                         : "#4FAE22",
//                                   }}
//                                 >
//                                   {document.is_active ? (
//                                     <ToggleOn />
//                                   ) : (
//                                     <ToggleOff />
//                                   )}
//                                 </IconButton>
//                               </Tooltip>

//                               <Tooltip title="Delete">
//                                 <IconButton
//                                   onClick={() =>
//                                     handleDelete(
//                                       document
//                                     )
//                                   }
//                                   sx={{
//                                     color:
//                                       "#c62828",
//                                   }}
//                                 >
//                                   <Delete />
//                                 </IconButton>
//                               </Tooltip>
//                             </Stack>
//                           </TableCell>
//                         </TableRow>
//                       )
//                     )}

//                     {paginatedDocuments.length ===
//                       0 && (
//                       <TableRow>
//                         <TableCell
//                           colSpan={10}
//                           align="center"
//                           sx={{
//                             py: 8,
//                           }}
//                         >
//                           <LibraryBooks
//                             sx={{
//                               fontSize: 60,
//                               color:
//                                 "#C4C7C7",
//                             }}
//                           />

//                           <Typography
//                             variant="h6"
//                             fontWeight={800}
//                             color="#062A4B"
//                           >
//                             No Curriculum Records
//                           </Typography>

//                           <Typography
//                             color="#6b7280"
//                           >
//                             No curriculum matches
//                             your current filters.
//                           </Typography>
//                         </TableCell>
//                       </TableRow>
//                     )}
//                   </TableBody>
//                 </Table>
//               </TableContainer>

//               <Divider />

//               <TablePagination
//                 component="div"
//                 count={
//                   filteredDocuments.length
//                 }
//                 page={page}
//                 onPageChange={(
//                   _,
//                   newPage
//                 ) =>
//                   setPage(newPage)
//                 }
//                 rowsPerPage={
//                   rowsPerPage
//                 }
//                 onRowsPerPageChange={(
//                   event
//                 ) => {
//                   setRowsPerPage(
//                     parseInt(
//                       event.target.value,
//                       10
//                     )
//                   );
//                   setPage(0);
//                 }}
//                 rowsPerPageOptions={[
//                   10,
//                   25,
//                   50,
//                   100,
//                 ]}
//               />
//             </>
//           )}
//         </Card>
//       </Box>
//     </Box>
//   );
// };


// export default AllCurriculumLis;